import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { AuthError } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

function formatAuthError(error: AuthError | string): string {
  if (typeof error === "string") return error;
  return error.message;
}

export function getRedirectPath(state: unknown): string {
  if (!state || typeof state !== "object" || !("from" in state)) return "/";
  const from = (state as {
    from?: string | { pathname?: string; search?: string; hash?: string };
  }).from;
  if (typeof from === "string" && from) return from;
  if (from && typeof from === "object" && from.pathname) {
    return `${from.pathname}${from.search ?? ""}${from.hash ?? ""}`;
  }
  return "/";
}

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);

    try {
      const result = await signIn(email, password);
      if (result.error) {
        setError(formatAuthError(result.error));
        return;
      }
      navigate(getRedirectPath(location.state), { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="w-full max-w-md space-y-6 rounded-lg border border-border/40 bg-background/50 p-6">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold text-foreground">STRONOM</h1>
          <p className="text-sm text-muted-foreground">
            Sign in to your account
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="login-password">Password</Label>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <Button
            type="button"
            className="w-full"
            disabled={submitting || !email || !password}
            onClick={handleSubmit}
          >
            {submitting ? "Please wait..." : "Sign in"}
          </Button>
        </div>
      </div>
    </div>
  );
}
